import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { NavItemDef, NavSectionDef } from "./nav-config";

/**
 * Translated labels for the nav config.
 *
 * `nav-config.ts` holds `name` as a plain English string because it is a plain
 * data module — it has no React and no i18next, and the route-matching tests
 * assert against it directly. Translating *in* that file would mean either
 * pulling React into the config or replacing `name` with keys and making every
 * consumer remember to resolve them.
 *
 * So resolution happens here, once, keyed by each item's stable `id` — never by
 * its English text. Renaming a row in English ("Browse" → "Explore") then can't
 * orphan the Vietnamese string, which is the failure mode keying by label
 * invites. `name` is passed as the i18next default value, so an id with no
 * translation yet renders exactly what it renders today: English.
 */
export function useNavLabels() {
  const { t } = useTranslation();

  return useMemo(() => {
    const itemLabel = (item: NavItemDef): string =>
      t(`nav.items.${item.id}`, { defaultValue: item.name });

    const sectionLabel = (section: NavSectionDef): string =>
      t(`nav.sections.${section.id}`, { defaultValue: section.label });

    /** My Space's expandable groups are user data, not config rows, so they
     *  carry no id to key off — they pass an explicit key. */
    const groupLabel = (id: string, fallback: string): string =>
      t(`nav.groups.${id}`, { defaultValue: fallback });

    /** "Expand Browse" / "Collapse Browse" — one string, translated, with the
     *  row name interpolated so the verb agrees with the language. */
    const expandLabel = (name: string, expanded: boolean): string =>
      expanded
        ? t("nav.actions.collapse", { name, defaultValue: `Collapse ${name}` })
        : t("nav.actions.expand", { name, defaultValue: `Expand ${name}` });

    return { itemLabel, sectionLabel, groupLabel, expandLabel };
  }, [t]);
}

export type NavLabels = ReturnType<typeof useNavLabels>;